import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import { Info, HelpCircle, Users, Briefcase } from 'lucide-react';
import { allCalculators } from '@/lib/calculator-data';
import type { SeoContent } from '@/lib/calculator-definitions';

const relatedToolSlugs = [
  'hourly-rate',
  'sponsorship-roi',
  'patreon-earnings-estimator',
  'digital-product-profit'
];

const relatedTools = allCalculators.filter(calc => relatedToolSlugs.includes(calc.slug));

export function CalculatorSeoContent({ seoContent }: { seoContent: SeoContent }) {
  if (!seoContent) {
    return null;
  }

  const { about, howTo, useCases, faqs, relatedTopics } = seoContent;

  return (
    <div className="container mx-auto px-4 mt-16 space-y-12">
      <div className="grid md:grid-cols-2 gap-8">
        {about && about.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3 font-headline text-2xl">
                <Info className="w-6 h-6 text-primary" />
                About the Tool
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-muted-foreground list-disc pl-5">
                {about.map((item, index) => <li key={index}>{item}</li>)}
              </ul>
            </CardContent>
          </Card>
        )}
        {useCases && useCases.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3 font-headline text-2xl">
                <Users className="w-6 h-6 text-primary" />
                Who is this for?
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3 text-muted-foreground list-disc pl-5">
                {useCases.map((item, index) => <li key={index}>{item}</li>)}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {howTo && howTo.length > 0 && (
        <div>
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-3 font-headline text-2xl">
                <Briefcase className="w-6 h-6 text-primary" />
                How to Use It
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3 text-muted-foreground list-decimal pl-5">
                  {howTo.map((item, index) => <li key={index}>{item}</li>)}
              </ol>
            </CardContent>
          </Card>
        </div>
      )}

      {faqs && faqs.length > 0 && (
        <div>
          <h2 className="font-headline text-3xl font-semibold tracking-tight mb-6 flex items-center gap-3">
            <HelpCircle className="w-8 h-8 text-primary" />
            Frequently Asked Questions
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger className="text-lg hover:no-underline">{faq.question}</AccordionTrigger>
                <AccordionContent className="text-base text-muted-foreground">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      )}
      
      {relatedTopics && relatedTopics.length > 0 && (
        <div>
          <h2 className="font-headline text-3xl font-semibold tracking-tight mb-6">Related Topics</h2>
          <div className="flex flex-wrap gap-2">
              {relatedTopics.map((topic, index) => (
                <Badge key={index} variant="secondary" className="text-sm font-normal">{topic}</Badge>
              ))}
          </div>
        </div>
      )}

      <div>
        <h2 className="font-headline text-3xl font-semibold tracking-tight mb-6">Related Tools</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {relatedTools.map((tool) => (
            <Link to={`/calculators/${tool.slug}`} key={tool.slug} className="group">
              <Card className="h-full hover:shadow-primary/20 hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <CardHeader>
                    <tool.icon className="w-8 h-8 text-primary mb-2" />
                    <CardTitle className="font-headline text-xl leading-tight">{tool.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{tool.description}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
